<script setup>
import MuralBirthdayCard from '@/components/rhdigital/mural/MuralBirthdayCard.vue'
import MuralComposer from '@/components/rhdigital/mural/MuralComposer.vue'
import MuralEventsCard from '@/components/rhdigital/mural/MuralEventsCard.vue'
import MuralPostCard from '@/components/rhdigital/mural/MuralPostCard.vue'
import MuralProfileHero from '@/components/rhdigital/mural/MuralProfileHero.vue'
import DialogConfirmDelet from '@/components/common/DialogConfirmDelet.vue'
import BasePagination from '@/components/tables/BasePagination.vue'
import { computed, onMounted, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { getUser } from '@/services/api/auth.js'
import { createMuralPost, deleteMuralPost, getMuralPosts, updateMuralPost } from '@/services/api/mural.js'
import { handleError } from '@/utils/errors.js'

const { t } = useI18n()
const composer = ref(null)
const posts = ref([])
const pagination = ref({ totalItems: 0, totalPages: 0, limit: 10 })
const currentPage = ref(1)
const itemsPerPage = ref(10)
const loadingMural = ref(false)
const publishing = ref(false)
const loadError = ref('')
const publishError = ref('')
const editingPostId = ref(null)
const updatingPostId = ref(null)
const deletingPostId = ref(null)
const postToDelete = ref(null)
const showDeleteConfirmation = ref(false)
const showPagination = computed(() => pagination.value.totalItems > itemsPerPage.value)

const profile = computed(() => ({
    name: getUser()?.fullname || '',
    role: t('components.rhdigital.mural.profile.role'),
    avatar: '/images/users/user1.jpg',
}))

const birthdays = computed(() => [
    {
        id: 1,
        name: 'Danilo',
        avatar: '/images/users/user5.jpg',
    },
    {
        id: 2,
        name: 'Marina',
        avatar: '/images/users/user12.jpg',
    },
    {
        id: 3,
        name: 'Rafael',
        avatar: '/images/users/user18.jpg',
    },
])

const nextEvent = computed(() => ({
    date: '15/08/26',
    title: t('components.rhdigital.mural.events.workshop'),
    time: '14:00 - 16:00',
}))

function extractPosts(response) {
    const data = response?.data?.items ?? response?.items ?? response?.data ?? response;
    return Array.isArray(data) ? data : [];
}

async function loadPosts({ silent = false, page = currentPage.value } = {}) {
    try {
        if (!silent) loadingMural.value = true;
        loadError.value = '';
        currentPage.value = page;

        const response = await getMuralPosts({
            page: currentPage.value,
            limit: itemsPerPage.value
        });
        posts.value = extractPosts(response);
        pagination.value = response?.page ?? {
            totalItems: posts.value.length,
            totalPages: posts.value.length ? 1 : 0,
            limit: itemsPerPage.value
        };
    } catch (error) {
        console.error(error);
        loadError.value = t('components.rhdigital.mural.states.loadError');
        message.error(await handleError(error, t));
    } finally {
        if (!silent) loadingMural.value = false;
    }
}

async function publishPost(post) {
    publishError.value = '';

    try {
        publishing.value = true;
        await createMuralPost({
            body: post.body,
            anexo: post.anexo
        });
        await loadPosts({ silent: true, page: 1 });
        composer.value?.reset();
        message.success(t('components.rhdigital.mural.states.publishSuccess'));
    } catch (error) {
        console.error(error);
        publishError.value = t('components.rhdigital.mural.states.publishError');
        message.error(await handleError(error, t));
    } finally {
        publishing.value = false;
    }
}

function canManagePost(post) {
    return post?.editavel === true;
}

function editPost(post) {
    if (canManagePost(post)) editingPostId.value = post.id;
}

async function savePost({ id, body, anexo }) {
    const post = posts.value.find(item => item.id === id);
    if (!canManagePost(post)) return;

    try {
        updatingPostId.value = id;
        await updateMuralPost({ id, body, anexo });
        await loadPosts({ silent: true });
        editingPostId.value = null;
        message.success(t('common.messages.success.update'));
    } catch (error) {
        console.error(error);
        message.error(await handleError(error, t));
    } finally {
        updatingPostId.value = null;
    }
}

function requestDelete(post) {
    if (!canManagePost(post)) return;
    postToDelete.value = post;
    showDeleteConfirmation.value = true;
}

async function confirmDelete() {
    if (!canManagePost(postToDelete.value)) return;

    try {
        deletingPostId.value = postToDelete.value.id;
        await deleteMuralPost({ id: postToDelete.value.id });
        await loadPosts({ silent: true });

        if (!posts.value.length && currentPage.value > 1) {
            await loadPosts({ silent: true, page: currentPage.value - 1 });
        }
        message.success(t('common.messages.success.itemDeletedSuccessfully'));
        showDeleteConfirmation.value = false;
        postToDelete.value = null;
    } catch (error) {
        console.error(error);
        message.error(await handleError(error, t));
    } finally {
        deletingPostId.value = null;
    }
}

async function changePage(page) {
    editingPostId.value = null;
    await loadPosts({ page });
}

onMounted(loadPosts)
</script>

<template>
    <section class="rhdigital-mural-page">
        <MuralProfileHero
            :profile="profile"
            :active-tab-index="0"
        />

        <div class="rhdigital-mural-page__grid">
            <main class="rhdigital-mural-page__feed">
                <MuralComposer
                    ref="composer"
                    :publishing="publishing"
                    :error="publishError"
                    @publish="publishPost"
                />

                <div v-if="loadingMural" class="rhdigital-mural-page__state" role="status">
                    <v-progress-circular indeterminate :size="28" :width="3" />
                    <span>{{ t('components.rhdigital.mural.states.loadingMural') }}</span>
                </div>

                <div v-else-if="loadError" class="rhdigital-mural-page__state rhdigital-mural-page__state--error" role="alert">
                    <span>{{ loadError }}</span>
                    <button type="button" @click="loadPosts()">
                        <span class="material-symbols-outlined">refresh</span>
                        {{ t('components.rhdigital.mural.states.retry') }}
                    </button>
                </div>

                <div v-else-if="posts.length === 0" class="rhdigital-mural-page__state">
                    <span class="material-symbols-outlined">dynamic_feed</span>
                    <span>{{ t('components.rhdigital.mural.states.empty') }}</span>
                </div>

                <template v-else>
                    <MuralPostCard
                        v-for="post in posts"
                        :key="post.id || post.created_at"
                        :post="post"
                        :can-manage="canManagePost(post)"
                        :editing="editingPostId === post.id"
                        :saving="updatingPostId === post.id"
                        :deleting="deletingPostId === post.id"
                        @edit="editPost"
                        @cancel-edit="editingPostId = null"
                        @save="savePost"
                        @delete="requestDelete"
                    />
                </template>

                <BasePagination
                    v-if="showPagination"
                    :fetch-data="loadPosts"
                    :total-items="pagination"
                    :current-page="currentPage"
                    :loading="loadingMural"
                    @page-change="changePage"
                />
            </main>

            <aside class="rhdigital-mural-page__aside">
                <MuralBirthdayCard :birthdays="birthdays" />
                <MuralEventsCard :event="nextEvent" />
            </aside>
        </div>

        <DialogConfirmDelet
            v-model="showDeleteConfirmation"
            @confirm="confirmDelete"
        >
            {{ t('components.rhdigital.mural.posts.deleteConfirmation') }}
        </DialogConfirmDelet>
    </section>
</template>

<style lang="scss" scoped>
.rhdigital-mural-page {
    min-height: calc(100vh - 83px);
    padding: 26px;
    background: #f6f8fd;

    &__grid {
        display: grid;
        grid-template-columns: minmax(0, 1fr) 266px;
        gap: 24px;
        margin-top: 24px;
    }

    &__feed,
    &__aside {
        display: flex;
        flex-direction: column;
        gap: 24px;
        min-width: 0;
    }

    &__state {
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 10px;
        min-height: 120px;
        padding: 24px;
        border: 1px solid #dde5f3;
        border-radius: 8px;
        background: #fff;
        color: #7d8ca4;
        text-align: center;

        > .material-symbols-outlined {
            font-size: 24px;
        }

        &--error {
            flex-direction: column;
            color: #dc2626;

            button {
                display: inline-flex;
                align-items: center;
                gap: 6px;
                height: 34px;
                padding: 0 12px;
                border: 0;
                border-radius: 8px;
                background: #f0efff;
                color: #5149e8;
                font-weight: 700;
            }
        }
    }
}

.dark-theme {
    .rhdigital-mural-page {
        background: #0a0e19;

        &__state {
            border-color: var(--borderColor);
            background: #0c1427;
            color: var(--bodyColor);

            &--error {
                color: #ff8b8b;

                button {
                    background: rgba(98, 91, 246, 0.16);
                    color: #aaa6ff;
                }
            }
        }
    }
}

@media (max-width: 1199px) {
    .rhdigital-mural-page {
        &__grid {
            grid-template-columns: minmax(0, 1fr);
        }

        &__aside {
            display: grid;
            grid-template-columns: repeat(2, minmax(0, 1fr));
        }
    }
}

@media (max-width: 767px) {
    .rhdigital-mural-page {
        padding: 16px;

        &__grid {
            gap: 16px;
            margin-top: 16px;
        }

        &__feed,
        &__aside {
            gap: 16px;
        }

        &__aside {
            grid-template-columns: minmax(0, 1fr);
        }
    }
}
</style>
